import {
	CirclePlus,
	Copy,
	Group,
	ListChevronsDownUp,
	LoaderCircle,
	RotateCcw,
	Send,
	SquaresUnite,
	Trash2,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Button } from "@/shared/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import {
	getDuplicateEntryTooltip,
	isGouldratner,
} from "@/shared/utils/firm";
import { DatePicker } from "@/v2/components/date-picker/DatePicker";
import { isCompleteDateRange } from "@/v2/lib/dates";
import type { FilterableAppId } from "../../hooks/useApplicationFilter";
import { ColumnPickerButton } from "./ColumnPickerButton";
import { type ClientMatterFilterSlice, FiltersPopover } from "./FiltersPopover";
import styles from "./HeaderBar.module.css";
import { type Matter, MatterSelector } from "./MatterSelector";
import { Timer } from "./Timer";

type ViewMode = "tracked" | "history" | "deleted" | "calendar" | "settings";

export interface HeaderBarProps {
	viewMode: ViewMode;
	date: Date;
	onDateChange: (d: Date) => void;
	dateRange?: { from: Date | undefined; to: Date | undefined };
	onDateRangeChange?: (r: {
		from: Date | undefined;
		to: Date | undefined;
	}) => void;
	leftContent?: React.ReactNode;
	timerRunning: boolean;
	elapsedSeconds: number;
	onStart: () => void;
	onStop: () => void;
	onReset: () => void;
	selectionCount: number;
	onAddEntry: () => void;
	onGroup?: () => void;
	onMerge?: () => void;
	onCollapse?: () => void;
	onDuplicate: () => void;
	onDelete: () => void;
	onUndo?: () => void;
	isUndoing?: boolean;
	onExport: () => void;
	isExporting?: boolean;
	actionInProgress?:
		| "group"
		| "merge"
		| "collapse"
		| "delete"
		| "duplicate"
		| "restore"
		| null;
	matters?: Matter[];
	hardcodedMatter?: string;
	onHardcodedMatterChange?: (matterId: string) => void;
	// Application filter props
	isApplicationVisible?: (appId: FilterableAppId) => boolean;
	onToggleApplication?: (appId: FilterableAppId) => void;
	onShowAllApplications?: () => void;
	onHideAllApplications?: () => void;
	// Client/matter filter — pass as a bundle or omit entirely
	clientMatter?: ClientMatterFilterSlice;
	// Team page overrides
	showTimer?: boolean;
	rightContent?: React.ReactNode;
}

const HeaderBar: React.FC<HeaderBarProps> = ({
	viewMode,
	date,
	onDateChange,
	dateRange,
	onDateRangeChange,
	leftContent,
	timerRunning,
	elapsedSeconds,
	onStart,
	onStop,
	onReset,
	selectionCount,
	onAddEntry,
	onGroup,
	onMerge,
	onCollapse,
	onDuplicate,
	onDelete,
	onUndo,
	isUndoing = false,
	onExport,
	isExporting = false,
	actionInProgress = null,
	matters = [],
	hardcodedMatter = "",
	onHardcodedMatterChange,
	isApplicationVisible,
	onToggleApplication,
	onShowAllApplications,
	onHideAllApplications,
	clientMatter,
	showTimer = true,
	rightContent,
}) => {
	const anyBusy = isExporting || actionInProgress !== null;

	// Defer loading spinners by 200ms so fast operations don't flash
	const [deferredAction, setDeferredAction] = useState(actionInProgress);
	const [deferredExporting, setDeferredExporting] = useState(isExporting);
	useEffect(() => {
		if (actionInProgress) {
			const id = setTimeout(() => setDeferredAction(actionInProgress), 200);
			return () => clearTimeout(id);
		}
		setDeferredAction(null);
	}, [actionInProgress]);
	useEffect(() => {
		if (isExporting) {
			const id = setTimeout(() => setDeferredExporting(true), 200);
			return () => clearTimeout(id);
		}
		setDeferredExporting(false);
	}, [isExporting]);
	const isTracked = viewMode === "tracked";
	const isHistory = viewMode === "history";
	const isDeleted = viewMode === "deleted";
	const isCalendar = viewMode === "calendar";

	const isSameCalendarDay = (a: Date, b: Date) =>
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate();

	const isRange = !!(dateRange?.from || dateRange?.to);
	const today = new Date();
	const isNotToday = isRange
		? !!(dateRange?.from && !isSameCalendarDay(dateRange.from, today)) ||
			!!(dateRange?.to && !isSameCalendarDay(dateRange.to, today))
		: !isSameCalendarDay(date, today);

	const selectedDateRange = isCompleteDateRange(dateRange)
		? dateRange
		: { from: date, to: date };

	const datePicker = onDateRangeChange ? (
		<DatePicker
			className="w-fit"
			dateRange={selectedDateRange}
			onDateChange={onDateChange}
			onDateRangeChange={onDateRangeChange}
			selectionMode="range"
		/>
	) : (
		<DatePicker
			className="w-fit"
			date={date}
			onDateChange={onDateChange}
			selectionMode="single"
		/>
	);

	const dateSelector = (
		<div className="relative">
			{isNotToday && (
				<span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-blue-500 border-2 border-card z-10" />
			)}
			{datePicker}
		</div>
	);

	const hasTopRow =
		!!leftContent ||
		(showTimer && (isTracked || isHistory || isDeleted)) ||
		(!showTimer && !!rightContent);

	const filtersTrigger =
		isApplicationVisible && onToggleApplication ? (
			<div className={styles.filterGroup}>
				<FiltersPopover
					isVisible={isApplicationVisible}
					onToggleApplication={onToggleApplication}
					onShowAllApps={onShowAllApplications || (() => {})}
					onHideAllApps={onHideAllApplications || (() => {})}
					clientMatter={clientMatter}
				/>
				{isGouldratner() && <ColumnPickerButton />}
			</div>
		) : null;

	return (
		<div
			className={styles.headerWrapper}
			style={
				!hasTopRow
					? { minHeight: "unset", paddingTop: 16, paddingBottom: 12 }
					: undefined
			}
		>
			{/* ============================================
          TOP ROW: SummaryCards | Timer + MatterSelector
          ============================================ */}
			{hasTopRow && (
				<div className={styles.topRow}>
					{/* Left: Summary Cards (40%) */}
					<div className={styles.topLeft}>{leftContent}</div>

					{/* Right: Timer + MatterSelector stacked (shown for entries and history) */}
					{(isTracked || isHistory || isDeleted) && showTimer && (
						<div className={styles.topRight}>
							{/* Timer - 180px, left-aligned */}
							{!isCalendar && (
								<div className={styles.timerControl}>
									<Timer
										elapsedSeconds={elapsedSeconds}
										isRunning={timerRunning}
										onStart={onStart}
										onStop={onStop}
										onReset={onReset}
									/>
								</div>
							)}

							{/* Hardcoded Matter Selector - 180px */}
							{onHardcodedMatterChange && (
								<div className={styles.matterControl}>
									<MatterSelector
										matters={matters}
										value={hardcodedMatter}
										onChange={onHardcodedMatterChange}
									/>
								</div>
							)}
						</div>
					)}
					{/* Custom right content (e.g., tab pills for team page) */}
					{!showTimer && rightContent && (
						<div
							className={styles.topRight}
							style={{ justifyContent: "center" }}
						>
							{rightContent}
						</div>
					)}
				</div>
			)}

			{/* ============================================
          BOTTOM ROW: ActionButtons+Matter | Timer+Add+Export
          ============================================ */}
			{isTracked && (
				<div className={styles.bottomRow}>
					{/* Left: Action Buttons (70%) */}
					<div className={styles.bottomLeft}>
						<TooltipProvider delayDuration={300}>
							<div className={styles.actionButtons}>
								{filtersTrigger}

								{/* Group 1: Merge, Collapse, Group */}
								<div className={styles.modifyGroup}>
									{onMerge && (
										<Tooltip>
											<TooltipTrigger asChild>
												<span>
													<Button
														variant="outline"
														size="icon"
														disabled={selectionCount < 2 || anyBusy}
														loading={deferredAction === "merge"}
														className={`${styles.actionButtonIcon} border-border text-foreground bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-card`}
														onClick={onMerge}
													>
														<SquaresUnite className="h-4 w-4" />
													</Button>
												</span>
											</TooltipTrigger>
											<TooltipContent>
												<p>Merge</p>
											</TooltipContent>
										</Tooltip>
									)}

									{onCollapse && (
										<Tooltip>
											<TooltipTrigger asChild>
												<span>
													<Button
														variant="outline"
														size="icon"
														disabled={selectionCount < 2 || anyBusy}
														loading={deferredAction === "collapse"}
														className={`${styles.actionButtonIcon} border-border text-foreground bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-card`}
														onClick={onCollapse}
													>
														<ListChevronsDownUp className="h-4 w-4" />
													</Button>
												</span>
											</TooltipTrigger>
											<TooltipContent>
												<p>Collapse</p>
											</TooltipContent>
										</Tooltip>
									)}

									{onGroup && (
										<Tooltip>
											<TooltipTrigger asChild>
												<span>
													<Button
														variant="outline"
														size="icon"
														disabled={selectionCount < 2 || anyBusy}
														loading={deferredAction === "group"}
														className={`${styles.actionButtonIcon} border-border text-foreground bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-card`}
														onClick={onGroup}
													>
														<Group className="h-4 w-4" />
													</Button>
												</span>
											</TooltipTrigger>
											<TooltipContent>
												<p>Group</p>
											</TooltipContent>
										</Tooltip>
									)}
								</div>

								{/* Group 2: Duplicate, Delete */}
								<div className={styles.modifyGroup}>
									<Tooltip>
										<TooltipTrigger asChild>
											<span>
												<Button
													variant="outline"
													size="icon"
													disabled={selectionCount === 0 || anyBusy}
													loading={deferredAction === "duplicate"}
													className={`${styles.actionButtonIcon} border-border text-foreground bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-card`}
													onClick={onDuplicate}
												>
													<Copy className="h-4 w-4" />
												</Button>
											</span>
										</TooltipTrigger>
										<TooltipContent>
											<p>{getDuplicateEntryTooltip()}</p>
										</TooltipContent>
									</Tooltip>

									<Tooltip>
										<TooltipTrigger asChild>
											<span>
												<Button
													variant="outline"
													size="icon"
													disabled={selectionCount === 0 || anyBusy}
													loading={deferredAction === "delete"}
													className={`${styles.actionButtonIcon} ${styles.deleteButton} disabled:opacity-40 disabled:cursor-not-allowed`}
													onClick={onDelete}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</span>
										</TooltipTrigger>
										<TooltipContent>
											<p>Delete</p>
										</TooltipContent>
									</Tooltip>
								</div>

								{/* Date Range Picker */}
								<div className={styles.datePickerInline}>{dateSelector}</div>
							</div>
						</TooltipProvider>
					</div>

					{/* Right: Add + Export (30%) */}
					<div className={styles.bottomRight}>
						<div className={styles.primaryActions}>
							<Button
								variant="default"
								className="h-12 bg-[var(--brand-dark-teal)] text-white hover:bg-[var(--brand-dark-teal)]/90 gap-2.5 px-5 font-medium shadow-xs hover:shadow-md transition-shadow flex items-center justify-center"
								onClick={onAddEntry}
							>
								<CirclePlus className="h-[18px] w-[18px]" strokeWidth={1.5} />
								Add
							</Button>
							<Button
								variant="default"
								onClick={onExport}
								disabled={anyBusy}
								className="h-12 bg-[var(--brand-teal)] text-white hover:bg-[var(--brand-teal)]/90 gap-2.5 px-5 font-medium shadow-xs hover:shadow-md transition-shadow flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
							>
								{deferredExporting ? (
									<LoaderCircle className="h-[18px] w-[18px] animate-spin" />
								) : (
									<Send className="h-[18px] w-[18px]" />
								)}
								{deferredExporting ? "Exporting..." : "Export"}
							</Button>
						</div>
					</div>
				</div>
			)}

			{/* History view: Show Filter + DatePicker (no action buttons, no Add/Export) */}
			{isHistory && (
				<div className={styles.bottomRow}>
					<div className={styles.bottomLeft}>
						<TooltipProvider delayDuration={300}>
							<div className={styles.actionButtons}>
								{filtersTrigger}

								{/* Date Range Picker - no wrapper margin since it's directly after Filter */}
								{dateSelector}
							</div>
						</TooltipProvider>
					</div>
					<div className={styles.bottomRight} />
				</div>
			)}

			{/* Deleted view: Filter + DatePicker on left, Undo button on right */}
			{isDeleted && (
				<div className={styles.bottomRow}>
					<div className={styles.bottomLeft}>
						<TooltipProvider delayDuration={300}>
							<div className={styles.actionButtons}>
								{filtersTrigger}
								{dateSelector}
							</div>
						</TooltipProvider>
					</div>
					<div className={styles.bottomRight}>
						<div className={styles.primaryActions}>
							<Button
								variant="default"
								onClick={onUndo}
								disabled={selectionCount === 0 || anyBusy}
								className="h-12 bg-[var(--brand-teal)] text-white hover:bg-[var(--brand-teal)]/90 gap-2.5 px-5 font-medium shadow-xs hover:shadow-md transition-shadow flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
							>
								{isUndoing ? (
									<LoaderCircle className="h-[18px] w-[18px] animate-spin" />
								) : (
									<RotateCcw className="h-[18px] w-[18px]" />
								)}
								{isUndoing ? "Restoring..." : "Restore"}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default HeaderBar;
